const PLAID_REDIRECT_QUERY_KEYS = [
  "plaid_success",
  "public_token",
  "plaid_error",
  "plaid_error_code",
  "plaid_error_message",
  "plaid_cancelled",
  "plaid_canceled",
  "plaid_cancel",
  "plaid_status",
  "plaid_exit_status",
] as const;

export type ConsumedPlaidRedirectParams = {
  hasPlaidRedirectParams: boolean;
  plaidSuccess: string | null;
  publicToken: string | null;
  plaidError: string | null;
  plaidErrorCode: string | null;
  plaidErrorMessage: string | null;
  plaidStatus: string | null;
  plaidExitStatus: string | null;
  plaidCancelled: string | null;
  plaidCanceled: string | null;
  plaidCancel: string | null;
};

export function consumePlaidRedirectQueryParams(): ConsumedPlaidRedirectParams {
  const url = new URL(window.location.href);
  const params = url.searchParams;

  const captured: ConsumedPlaidRedirectParams = {
    hasPlaidRedirectParams: PLAID_REDIRECT_QUERY_KEYS.some((key) =>
      params.has(key),
    ),
    plaidSuccess: params.get("plaid_success"),
    publicToken: params.get("public_token"),
    plaidError: params.get("plaid_error"),
    plaidErrorCode: params.get("plaid_error_code"),
    plaidErrorMessage: params.get("plaid_error_message"),
    plaidStatus: params.get("plaid_status"),
    plaidExitStatus: params.get("plaid_exit_status"),
    plaidCancelled: params.get("plaid_cancelled"),
    plaidCanceled: params.get("plaid_canceled"),
    plaidCancel: params.get("plaid_cancel"),
  };

  if (captured.hasPlaidRedirectParams) {
    for (const key of PLAID_REDIRECT_QUERY_KEYS) {
      params.delete(key);
    }
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, document.title, next);
  }

  return captured;
}

type PlaidRedirectOutcome =
  | { kind: "none" }
  | { kind: "success"; publicToken: string | null }
  | { kind: "cancel"; message: string }
  | { kind: "error"; message: string };

export function derivePlaidRedirectOutcome(
  redirectParams: ConsumedPlaidRedirectParams,
): PlaidRedirectOutcome {
  if (!redirectParams.hasPlaidRedirectParams) {
    return { kind: "none" };
  }

  const status = redirectParams.plaidStatus?.toLowerCase();
  const exitStatus = redirectParams.plaidExitStatus?.toLowerCase();
  const plaidErrorCode = redirectParams.plaidErrorCode?.toLowerCase();
  const plaidError = redirectParams.plaidError?.toLowerCase();
  const cancelledViaStatus =
    status === "cancel" ||
    status === "cancelled" ||
    status === "canceled" ||
    exitStatus === "cancel" ||
    exitStatus === "cancelled" ||
    exitStatus === "canceled";
  const cancelledViaFlag =
    redirectParams.plaidCancelled === "true" ||
    redirectParams.plaidCanceled === "true" ||
    redirectParams.plaidCancel === "true";
  const cancelledViaError =
    plaidError?.includes("cancel") || plaidErrorCode?.includes("cancel");

  if (
    redirectParams.plaidSuccess === "false" ||
    cancelledViaStatus ||
    cancelledViaFlag ||
    cancelledViaError
  ) {
    return {
      kind: "cancel",
      message: "Plaid linking was canceled. Please try again.",
    };
  }

  if (redirectParams.plaidError || redirectParams.plaidErrorCode) {
    return {
      kind: "error",
      message:
        redirectParams.plaidErrorMessage ||
        "Plaid linking failed. Please try again.",
    };
  }

  if (redirectParams.plaidSuccess === "true") {
    return {
      kind: "success",
      publicToken: redirectParams.publicToken,
    };
  }

  return { kind: "none" };
}

export const DEFAULT_PLAID_RETURN_PATH = "/home/wealth";

const PLAID_RETURN_PATH_BASE = "https://wavvest.invalid";

export function hasPlaidRedirectQueryParams(
  params: URLSearchParams = new URLSearchParams(window.location.search),
): boolean {
  return PLAID_REDIRECT_QUERY_KEYS.some((key) => params.has(key));
}

export function sanitizePlaidReturnPath(returnPath?: string | null): string {
  const fallback = DEFAULT_PLAID_RETURN_PATH;
  if (!returnPath) {
    return fallback;
  }
  const trimmed = returnPath.trim();
  if (!trimmed.startsWith("/") || trimmed.startsWith("//") || trimmed.includes("\\")) {
    return fallback;
  }
  try {
    const parsed = new URL(trimmed, PLAID_RETURN_PATH_BASE);
    if (parsed.origin !== PLAID_RETURN_PATH_BASE || !parsed.pathname.startsWith("/home")) {
      return fallback;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return fallback;
  }
}

export function buildPlaidAppRedirectUrl(
  origin: string,
  returnPath?: string | null,
): string {
  const url = new URL(sanitizePlaidReturnPath(returnPath), origin);
  url.searchParams.set("plaid_success", "true");
  return url.toString();
}

export function currentLocationMatchesPlaidReturnPath(
  currentPathname: string,
  returnPath?: string | null,
): boolean {
  const expected = new URL(sanitizePlaidReturnPath(returnPath), PLAID_RETURN_PATH_BASE);
  return currentPathname === expected.pathname;
}

export function buildPlaidPublicRedirectUrl(
  plaidApiBaseUrl: string,
  linkToken: string,
  redirectUrl: string,
): string {
  const plaidPublicUrl = new URL(plaidApiBaseUrl);
  const basePath = plaidPublicUrl.pathname.endsWith("/")
    ? plaidPublicUrl.pathname
    : `${plaidPublicUrl.pathname}/`;
  plaidPublicUrl.pathname = `${basePath}public/`;
  plaidPublicUrl.searchParams.set("link_token", linkToken);
  plaidPublicUrl.searchParams.set("redirect_uri", redirectUrl);
  return plaidPublicUrl.toString();
}
